import * as Toast from '@radix-ui/react-toast';
import type { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';

import { Portal } from '#src/atoms/layout/Portal/index.tsx';
import { theme } from '#src/style/theme.tsx';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;
const slideUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;
const swipeOutUp = keyframes`
  from {
    transform: translateY(var(--radix-toast-swipe-end-y));
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;
const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;
const swipeOutToRight = keyframes`
  from {
    transform: translateX(var(--radix-toast-swipe-end-x));
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const Root = styled(Toast.Root)`
  pointer-events: auto;
  &[data-state='open'] {
    animation: ${slideDown} 700ms var(--ease-out-slide);
  }
  &[data-swipe='move'] {
    transform: translate(
      var(--radix-toast-swipe-move-x, 0),
      var(--radix-toast-swipe-move-y, 0)
    );
  }
  &[data-swipe='cancel'] {
    transform: translate(0, 0);
    transition: transform 200ms ease-out;
  }
  &[data-state='closed'] {
    animation: ${slideUp} 250ms ease-out;
  }
  &[data-swipe='end'] {
    animation: ${swipeOutUp} 250ms ease-out;
  }
  &:focus {
    outline: none;
  }

  &.position-bottom {
    &[data-state='open'] {
      animation: ${slideInFromRight} 700ms var(--ease-out-slide);
    }
    &[data-state='closed'] {
      animation: ${slideOutToRight} 250ms ease-out;
    }
    &[data-swipe='end'] {
      animation: ${swipeOutToRight} 250ms ease-out;
    }
  }
`;

const Viewport = styled(Toast.Viewport)`
  position: fixed;
  z-index: ${theme.zIndex.snackbar};
  display: flex;
  flex-direction: column;
  pointer-events: none;

  &.position-top {
    top: 0;
    left: 0;
    right: 0;
    padding: var(--double-unit);
  }

  &.position-top-right {
    top: var(--app-header-height-desktop);
    right: 0;
    align-items: flex-end;
    padding: var(--double-unit);
  }

  &.position-bottom {
    right: 0;
    bottom: calc(var(--bottom-bar-height-mobile, 0px) + var(--unit));
    padding: var(--double-unit) 0 var(--double-unit) var(--double-unit);

    align-items: center;
  }
`;

type Position = 'top' | 'top-right' | 'bottom';

type Props = {
  className?: string;
  children: ReactNode;
  position?: Position;
} & Toast.ToastProps;

export const ToastBase = ({
  className,
  children,
  position = 'top',
  ...props
}: Props) => {
  const swipeDirection = position === 'bottom' ? 'right' : 'up';

  return (
    <Toast.Provider swipeDirection={swipeDirection} swipeThreshold={24}>
      <Root
        {...props}
        className={[className, `position-${position}`]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </Root>
      <Portal id="snackbar">
        <Viewport className={`position-${position}`} />
      </Portal>
    </Toast.Provider>
  );
};
